import { firestore } from "firebase";
import {
  AddressInfo,
  NameInfo,
  VisibilityRestriction,
  ProjectConclusionState,
  ProjectConclusion,
  ApprovalResult,
  ExtendedProjectType,
  ExtendedVisibilityRestriction,
  containsLeakDetectionInfo,
  containsOnlyLeakDetectionInfo,
  isPortalOnlyProject,
} from "./ProjectDetails";
import { RecordInfo } from "./RecordInfo";
import {
  ProjectType,
  isLeakDetectionProject,
} from "../repository/ProjectRepository";
import LeakDetectionProjectOverview from "./LeakDetectionProjectOverview";
import {
  DryingProjectOverview,
  DamagePatternConclusionState,
} from "./DryingProjectOverview";
import { parseReportDate, roundTimestampToMinutes } from "@/utils/DateHelper";
import { DateTime } from "luxon";

export interface ContactPersonOverview extends NameInfo {
  id: string;
  contactPersonNumber: number;
}

export interface ProjectApproval {
  recordInfo: RecordInfo;
  approvalResult: ApprovalResult;
}

export enum ProjectListType {
  RUNNING_PROJECTS = "RUNNING_PROJECTS",
  FINISHED_PROJECTS = "FINISHED_PROJECTS",
  ARCHIVED_PROJECTS = "ARCHIVED_PROJECTS",
  DELETED_PROJECTS = "DELETED_PROJECTS",
}

interface ProjectOverview {
  id: string;
  damageId?: string;
  projectOwner?: string;
  projectOwnerStaffNumber?: string;
  projectSupervisorStaffNumber?: string;
  projectName: string;
  projectNumber: string;
  projectNameErp?: string;
  projectNumberIres?: string;
  projectSupervisorId?: string;
  internalProjectNumber: number;
  startDate: firestore.Timestamp;
  wasFinalReportSend: boolean;
  isFinished: boolean;
  location?: AddressInfo;
  endDate?: firestore.Timestamp | null;
  projectApproval?: ProjectApproval;
  contactPersons: Array<ContactPersonOverview>;

  /**
   * `projectScope` is deprecated and should not be used. Please see `ProjectOverview.extendedProjectScope`.
   */
  projectScope: ProjectType[];
  extendedProjectScope?: ExtendedProjectType[];
  branch?: string;
  /**
   * `visibilityRestrictions` is deprecated and should not be used. Please see `ProjectOverview.extendedVisibilityRestrictions`.
   */
  visibilityRestrictions?: VisibilityRestriction[];
  extendedVisibilityRestrictions?: ExtendedVisibilityRestriction[];
  projectTypeId?: number;
  allAssignmentsClosed?: boolean;
  /**
   * `projectState` is deprecated and should not be used. Please see `Project.conclusion`.
   */
  projectState?: ProjectConclusionState | null;
  projectStartedBy?: RecordInfo;
  deleted?: boolean;
  lastOperationAt?: firestore.Timestamp;
  conclusion?: ProjectConclusion | null;

  projectListType?: ProjectListType;
  testProject?: boolean;
}

export default ProjectOverview;

export function getProjectConclusionState(
  projectOverview: ProjectOverview
): ProjectConclusionState | null {
  return projectOverview.conclusion?.conclusionState ??
    projectOverview.projectState ??
    null;
}

/**
 * Decides whether an overview row describes a finished project.
 *
 * The row is not trusted to sit in the right collection, so the state is re-derived from its own
 * fields. `isFinished` is accepted as one of those fields because it is what Cloud's
 * `createProjectOverview` writes when it rebuilds a row, while `projectConclusionComplete` (which
 * the checks below look for) is not written on that path at all: without it, a rebuilt row of a
 * finished project reads as running. Every branch stays gated on `endDate`, and reopening a project
 * clears `endDate`, so a stale `isFinished` cannot make a reopened project look finished.
 */
export function isProjectFinished(projectOverview: ProjectOverview): boolean {
  if (isLeakDetectionProject(projectOverview.id)) {
    const leakDetectionProjectOverview = projectOverview as LeakDetectionProjectOverview;
    return (
      (projectOverview.endDate &&
        (leakDetectionProjectOverview.leakDetectionConclusionComplete ||
          leakDetectionProjectOverview.projectApproval?.approvalResult ===
            ApprovalResult.REJECTED ||
          projectOverview.isFinished === true)) ??
      false
    );
  } else {
    const dryingProjectOverview = projectOverview as DryingProjectOverview;
    const projectConclusionState = getProjectConclusionState(projectOverview);

    return (
      (dryingProjectOverview.endDate &&
        (dryingProjectOverview.projectConclusionComplete ||
          dryingProjectOverview.damagePatternConclusionState ===
            DamagePatternConclusionState.NO_CONTRACT ||
          dryingProjectOverview.damagePatternConclusionState ===
            DamagePatternConclusionState.NO_DRYING_NECESSARY ||
          projectConclusionState !==
            null ||
          dryingProjectOverview.projectApproval?.approvalResult ===
          ApprovalResult.REJECTED ||
          containsOnlyLeakDetectionInfo(projectOverview) ||
          dryingProjectOverview.isFinished === true)) ??
      false
    );
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getExternalReportDate(step: any) {
  if (!step) {
    return 0;
  }

  if (!step.downloadRefExternalPdfReport) {
    return 0;
  }

  const reportRef = step.downloadRefExternalPdfReport;

  const reportDateString = reportRef.substring(
    reportRef.length - 14,
    reportRef.length - 4
  );

  try {
    const reportDate = parseReportDate(reportDateString);

    if (reportDate) {
      return reportDate.getTime();
    }
  } catch (e) {
    return 0;
  }

  return 0;
}

/**
 * Whether the project row gets the red "Kein Bericht" status and matches the "Bericht fehlt" filter.
 * Mirrors IRES Project's `isMissingReport` (DryingProjectOverview.kt), which decides the same on the device.
 */
export function isMissingReports(projectOverview: ProjectOverview): boolean {
  const leakDetectionProject = isLeakDetectionProject(projectOverview.id);

  if (leakDetectionProject) {
    const leakDetectionProjectOverview = projectOverview as LeakDetectionProjectOverview;
    const leakDetectionResult = leakDetectionProjectOverview.leakDetectionProjectSteps?.leakDetectionResult;

    return !!leakDetectionResult && !leakDetectionResult.downloadRefExternalPdfReport;
  } else {
    const dryingProjectOverview = projectOverview as DryingProjectOverview;

    const projectSteps = dryingProjectOverview.dryingProjectSteps;

    if (projectSteps) {
      const projectResult = projectSteps.projectResult;
      const damageAssessmentResult = projectSteps.damageAssessmentResult;
      const leakDetectionResult = projectSteps.leakDetectionResult;

      if (containsOnlyLeakDetectionInfo(dryingProjectOverview)) {
        return !!leakDetectionResult && !leakDetectionResult.downloadRefExternalPdfReport;
      }

      // Without leak detection in the scope, step 5 comes from a leak detection done in another
      // project, which IRES Project writes without a report ref: it neither has nor misses a report.
      const ownLeakDetectionResult =
        leakDetectionResult &&
        (containsLeakDetectionInfo(dryingProjectOverview) || leakDetectionResult.downloadRefExternalPdfReport)
          ? leakDetectionResult
          : undefined;

      if (projectResult) {
        return !projectResult.downloadRefExternalPdfReport;
      } else if (damageAssessmentResult) {
        if (damageAssessmentResult.downloadRefExternalPdfReport) {
          return false;
        }
      } else if (ownLeakDetectionResult) {
        if (ownLeakDetectionResult.downloadRefExternalPdfReport) {
          return false;
        }
      }

      if (damageAssessmentResult || ownLeakDetectionResult) {
        const reportDates = [
          getExternalReportDate(projectResult),
          ...(projectSteps.dryingSetupCompletions?.map((step) =>
            getExternalReportDate(step)
          ) ?? []),
        ].sort();

        const latestReportDate = reportDates[reportDates.length - 1];
        const damageAssessmentConclusionDate =
          projectSteps.damageAssessmentResult?.creationTimestamp;

        if (latestReportDate && damageAssessmentConclusionDate) {
          const damageAssessmentEndDate = DateTime.fromISO(
            damageAssessmentConclusionDate
          );

          try {
            const timeDiff =
              roundTimestampToMinutes(latestReportDate) -
              roundTimestampToMinutes(damageAssessmentEndDate.toMillis());

            const oneMinuteInMillis = 60000;

            if (timeDiff >= -oneMinuteInMillis) {
              return false;
            }
          } catch (e) {
            return false;
          }
        }

        return true;
      }

      return false;
    }
  }

  return false;
}

export function isProjectOverviewWebsiteOnly(projectOverview: ProjectOverview) {
  return isPortalOnlyProject(projectOverview);
}

export function isProjectDeleted(projectOverview: ProjectOverview) {
  const projectConclusionState = getProjectConclusionState(projectOverview);

  return (
    projectOverview.deleted ||
    projectConclusionState === ProjectConclusionState.DELETED
  )
}